/**
 * Which section a profile OPENS on when the URL does not name one (slice P1a).
 *
 * <b>29% of profiles that have an audience have published zero posts</b>, so the old fixed
 * `discussions` default handed a large minority of visitors a blank page as their first impression of a
 * member. The counts that decide it are already on the page, so this costs no request.
 *
 * ## Amended 2026-08-19 — the LARGEST content section wins, not the first non-empty one
 *
 * `EMPTY-STATES-12` / `PROFILE-10`. The first-non-empty rule asked only whether a member had posted at
 * all, so a member with **2 posts and 2,630 thoughts** opened on a two-item list standing in front of
 * everything they had actually written. The question a visitor is asking is "where is this member's
 * work", not "have they ever posted".
 *
 * So `discussions` and `arguments` compete on COUNT, ties going to `discussions` so an evenly balanced
 * profile lands exactly where it always did. `followers` deliberately does NOT compete: it counts
 * PEOPLE rather than work, and it routinely runs an order of magnitude above both content counts, so
 * letting it compete on size would make it the landing section for very nearly every established
 * account. It keeps the last-resort non-zero role it has always had. See
 * `designs/07-archive/runs/2026-08-19-ux-critique-remediation-wave-5/batch-7-profile-landing.md` §3 — the
 * narrowing is a parked question, not a silent choice.
 *
 * Exactly one profile shape moves: at least one post AND more thoughts than posts. Zero-post profiles,
 * entirely empty profiles and explicitly requested sections are unchanged.
 *
 * ## This is the CLIENT half of a rule the server also implements
 *
 * `UserController.resolveLandingSection` is the twin, and `user.jte` renders `data-section` from it. On
 * an ordinary page load the server has already decided and the island simply reads its answer out of the
 * dataset — the two cannot disagree, which is the point. This module exists for the two cases the
 * server's answer does not cover:
 *
 * - a mount whose `data-section` is absent (the component gallery, a future embed);
 * - a client-side location with no section segment, i.e. Back to a bare `/user/{name}`.
 *
 * The pair is pinned by `UserControllerTests.landingSectionPreferenceOrderMirrorsTheFrontend`, which
 * reads BOTH constants below out of THIS file rather than restating them. Nothing links the two
 * languages at compile time, and a divergence is invisible in every appearance-based test: the server
 * renders one section, the island mounts another over it, and what a crawler indexes on a cold cache is
 * the render that got discarded.
 *
 * ## Read the INITIAL counts, never the live ones
 *
 * Callers must pass the counts the page mounted with. Resolving against state that a bootstrap fetch
 * updates would move the landing section a second after first paint, which is the mid-flight section
 * swap this rule exists to prevent, arriving late instead of early.
 *
 * Design: `designs/01-now/2026-08-11-profile-destination-slice-design.md` §9, §14.2, as amended by
 * `designs/07-archive/runs/2026-08-19-ux-critique-remediation-wave-5/batch-7-profile-landing.md`.
 */

/**
 * Tie-break and fall-through order. Mirrored in `UserController.LANDING_SECTION_PREFERENCE_ORDER`
 * — if you reorder this, reorder that, in the same commit.
 */
export const LANDING_SECTION_PREFERENCE_ORDER = ["discussions", "arguments", "followers"] as const;

/**
 * The sections that hold a member's own WORK, and the only ones that compete on size. A subset of the
 * order above, in the same order, so the size comparison and the tie-break cannot disagree. Mirrored in
 * `UserController.LANDING_SECTION_CONTENT_SECTIONS`.
 */
export const LANDING_SECTION_CONTENT_SECTIONS = ["discussions", "arguments"] as const;

/** Where a profile with none of the above lands. Its empty state is an invitation, not a dead end. */
export const LANDING_SECTION_FALLBACK = "discussions";

export type LandingSectionCounts = {
    discussionsCount?: number | null;
    argumentsCount?: number | null;
    followersCount?: number | null;
};

function countFor(counts: LandingSectionCounts, section: string): number {
    switch (section) {
        case "discussions":
            return counts.discussionsCount ?? 0;
        case "arguments":
            return counts.argumentsCount ?? 0;
        case "followers":
            return counts.followersCount ?? 0;
        default:
            return 0;
    }
}

export function resolveLandingSection(counts: LandingSectionCounts): string {
    // The largest content section, ties going to the earlier entry in the preference order — so a
    // strict `>` here, never `>=`.
    let largestContentSection: string | null = null;
    let largestContentCount = 0;
    for (const candidate of LANDING_SECTION_CONTENT_SECTIONS) {
        const count = countFor(counts, candidate);
        if (count > largestContentCount) {
            largestContentSection = candidate;
            largestContentCount = count;
        }
    }
    if (largestContentSection !== null) {
        return largestContentSection;
    }

    // No work published at all: fall through the remaining preference order on presence, exactly as
    // before. `followers` is a count of people, so it never competes with the two above on size.
    for (const candidate of LANDING_SECTION_PREFERENCE_ORDER) {
        if (countFor(counts, candidate) > 0) {
            return candidate;
        }
    }
    return LANDING_SECTION_FALLBACK;
}
